import { useState, useRef } from "react";
import { Loader2, Flame, FileText, Clock, CheckCircle } from "lucide-react";

const COLUMNS = [
  { id: "new", label: "Discovery", color: "border-t-blue-500", icon: "+" },
  { id: "contacted", label: "Contacted", color: "border-t-yellow-500", icon: null },
  { id: "booked", label: "Call Booked", color: "border-t-indigo-500", icon: null },
  { id: "qualified", label: "Blueprint Ready", color: "border-t-emerald-500", icon: null },
  { id: "proposal", label: "Proposal Sent", color: "border-t-purple-500", icon: null },
  { id: "closed_won", label: "Won", color: "border-t-green-600", icon: null },
  { id: "closed_lost", label: "Lost", color: "border-t-red-500", icon: null },
];

export default function OpsPipeline({ leads, onStatusChange, onOpenLead, loading }) {
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const dragRef = useRef(null);

  const handleDragStart = (e, leadId) => {
    setDraggedId(leadId);
    dragRef.current = leadId;
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, colId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverCol(colId);
  };

  const handleDrop = (e, colId) => {
    e.preventDefault();
    const leadId = dragRef.current;
    if (leadId) {
      onStatusChange(leadId, colId);
    }
    setDraggedId(null);
    setDragOverCol(null);
    dragRef.current = null;
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverCol(null);
    dragRef.current = null;
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#0033CC]" /></div>;
  }

  return (
    <div className="flex gap-3 overflow-x-auto pb-4" data-testid="ops-pipeline">
      {COLUMNS.map(col => {
        const colLeads = leads.filter(l => l.status === col.id);
        return (
          <div
            key={col.id}
            className={`min-w-[220px] flex-1 bg-slate-50 border border-slate-200 border-t-2 ${col.color} ${dragOverCol === col.id ? "bg-blue-50/50" : ""}`}
            onDragOver={(e) => handleDragOver(e, col.id)}
            onDrop={(e) => handleDrop(e, col.id)}
            onDragLeave={() => setDragOverCol(null)}
            data-testid={`pipeline-col-${col.id}`}
          >
            <div className="px-3 py-2.5 flex items-center justify-between border-b border-slate-200 bg-white">
              <span className="text-xs font-['Outfit'] font-medium text-[#0F172A]">{col.label}</span>
              <span className="text-xs font-['JetBrains_Mono'] text-slate-400 bg-slate-100 px-1.5 py-0.5">{colLeads.length}</span>
            </div>
            <div className="p-2 space-y-2 min-h-[200px]">
              {colLeads.map(lead => {
                const hasReport = !!lead.audit_data?.report;
                const reportTier = lead.audit_data?.report?.tier || lead.report_tier || "";
                return (
                  <div
                    key={lead.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, lead.id)}
                    onDragEnd={handleDragEnd}
                    onClick={() => onOpenLead(lead.id)}
                    className={`bg-white border border-slate-200 p-3 cursor-pointer hover:border-slate-300 transition-all ${draggedId === lead.id ? "opacity-40 scale-95" : ""}`}
                    data-testid={`pipeline-card-${lead.id}`}
                  >
                    <div className="text-sm font-['IBM_Plex_Sans'] font-medium text-[#0F172A] truncate">{lead.name}</div>
                    <div className="text-xs text-slate-400 font-['IBM_Plex_Sans'] truncate">{lead.company || lead.email}</div>
                    <div className="flex items-center justify-between mt-2">
                      {lead.deal_score > 0 ? (
                        <span className={`text-xs font-['JetBrains_Mono'] font-bold flex items-center gap-1 ${lead.deal_score >= 60 ? "text-red-600" : lead.deal_score >= 40 ? "text-amber-600" : "text-slate-500"}`}>
                          {lead.deal_score >= 60 && <Flame className="w-3 h-3" />}{lead.deal_score}
                        </span>
                      ) : lead.score > 0 ? (
                        <span className="text-xs font-['JetBrains_Mono'] text-[#0033CC]">{lead.score}/100</span>
                      ) : <span />}
                      {lead.deal_value > 0 && (
                        <span className="text-xs font-['JetBrains_Mono'] text-emerald-600">${lead.deal_value.toLocaleString()}</span>
                      )}
                    </div>
                    {/* Blueprint indicators */}
                    <div className="flex items-center gap-1.5 mt-2">
                      {hasReport ? (
                        <span className="text-[10px] font-['JetBrains_Mono'] px-1.5 py-0.5 flex items-center gap-1 bg-emerald-50 text-emerald-700">
                          <CheckCircle className="w-2.5 h-2.5" />Blueprint
                        </span>
                      ) : lead.audit_id ? (
                        <span className="text-[10px] font-['JetBrains_Mono'] px-1.5 py-0.5 bg-amber-50 text-amber-700 flex items-center gap-1">
                          <Clock className="w-2.5 h-2.5" />Discovery
                        </span>
                      ) : null}
                      {lead.tags?.length > 0 && lead.tags.slice(0, 1).map(t => (
                        <span key={t} className="text-[10px] font-['JetBrains_Mono'] px-1.5 py-0.5 bg-slate-100 text-slate-500">{t}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
